import { queryKeys } from "@lib/api/queryKeys";
import { useToast } from "@lib/hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { API_ENDPOINTS } from "../config/constants";
import type { PointageContext, PointageSelfVariables, SelfPointage } from "../types/pointageSelf";

interface UsePointageContextOptions {
  /** Disable the query (e.g. while the widget is hidden). */
  enabled?: boolean;
  /** Date ciblée (YYYY-MM-DD) — rétroactif DEMI_JOURNEE. Défaut : aujourd'hui (backend). */
  date?: string;
}

/**
 * Hook for the self-service pointage context (GET /pointage/me/context).
 * Exposes the current user's mode, expected slots and pointages — for today
 * or any past date (rétroactif DEMI_JOURNEE via `?date=YYYY-MM-DD`).
 */
export function usePointageContext(options: UsePointageContextOptions = {}) {
  const { enabled = true, date = "" } = options;

  return useQuery({
    queryKey: queryKeys.rh.pointage.context(date),
    queryFn: async () => {
      const url = date
        ? `${API_ENDPOINTS.SELF_CONTEXT}?date=${encodeURIComponent(date)}`
        : API_ENDPOINTS.SELF_CONTEXT;
      const response = await api.get<PointageContext>(url);
      return response.data;
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

/**
 * Mutation for the self-service pointage (POST /pointage/me).
 * The backend resolves the expected slot from the planning (PC/VARIABLE),
 * or uses the explicit bloc/type for retroactive DEMI_JOURNEE.
 */
export function usePointageSelf() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (variables: PointageSelfVariables) => {
      const response = await api.post<SelfPointage>(API_ENDPOINTS.SELF_SUBMIT, variables);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rh.pointage.all() });
      toast.success("Pointage enregistré");
    },
    onError: (error: { response?: { data?: { message?: string; detail?: string } } }) => {
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Une erreur est survenue lors du pointage";
      toast.error(message);
    },
  });
}
